import type { CarState } from './types';
import type { Track } from './track';

interface CarRaceState {
  nextCp: number;
  lap: number;
  lapStartTime: number;
  lastLapTime: number;
  bestLapTime: number;
  wrongWay: boolean;
  progress: number;
  prevX: number;
  prevY: number;
  hint: number;
}

/**
 * Suit la progression de chaque voiture sur le circuit : franchissement de portes
 * ORDONNÉES (anti-coupe), comptage des tours, contre-sens, et classement par
 * progression curviligne. Pur (zéro Phaser), déterministe.
 */
export class RaceProgress {
  private readonly track: Track;
  private readonly states: CarRaceState[];

  constructor(track: Track, cars: CarState[]) {
    this.track = track;
    const gateCount = track.gates.length;
    this.states = cars.map((c) => {
      const hint = track.nearestSampleIndex(c.x, c.y);
      // Prochaine porte ATTENDUE = la première porte devant la position de départ. Sur une grille,
      // une voiture peut spawner au-delà de la porte 1 : coder nextCp=1 en dur la laisserait à
      // jamais en retard d'un tour. (Pour un spawn juste après la ligne, ça redonne bien 1.)
      const s = track.arcLengthAt(c.x, c.y, hint);
      let nextCp = 0;
      while (nextCp < gateCount && track.gates[nextCp].s <= s) nextCp++;
      return {
        nextCp: nextCp % gateCount,
        lap: 0,
        lapStartTime: 0,
        lastLapTime: 0,
        bestLapTime: 0,
        wrongWay: false,
        progress: 0,
        prevX: c.x,
        prevY: c.y,
        hint,
      };
    });
  }

  update(cars: CarState[], time: number): void {
    const gateCount = this.track.gates.length;

    for (let i = 0; i < cars.length; i++) {
      const car = cars[i];
      const st = this.states[i];

      // Franchissement de la porte ATTENDUE uniquement (ordre imposé => anti-coupe).
      const gate = this.track.gates[st.nextCp];
      if (segmentsIntersect(st.prevX, st.prevY, car.x, car.y, gate.ax, gate.ay, gate.bx, gate.by)) {
        const forward = car.vx * gate.tx + car.vy * gate.ty > 0;
        if (forward) {
          const crossed = st.nextCp;
          st.nextCp = (st.nextCp + 1) % gateCount;
          if (crossed === 0) {
            // On vient de repasser la ligne après avoir validé toutes les portes => tour bouclé.
            st.lap += 1;
            st.lastLapTime = time - st.lapStartTime;
            st.bestLapTime = st.bestLapTime === 0 ? st.lastLapTime : Math.min(st.bestLapTime, st.lastLapTime);
            st.lapStartTime = time;
          }
        }
      }

      // Contre-sens : la voiture roule contre la tangente locale de la piste.
      st.hint = this.track.nearestSampleIndex(car.x, car.y, st.hint);
      const tan = this.track.tangentAt(st.hint);
      const along = car.vx * tan.x + car.vy * tan.y;
      st.wrongWay = car.speed > 20 && along < 0;

      // Progression scalaire monotone pour le classement.
      const s = this.track.arcLengthAt(car.x, car.y, st.hint);
      st.progress = st.lap * this.track.length + s;

      st.prevX = car.x;
      st.prevY = car.y;

      // Recopie dans l'état voiture (sérialisable / rendu).
      car.lap = st.lap;
      car.checkpointIdx = (st.nextCp - 1 + gateCount) % gateCount;
      car.wrongWay = st.wrongWay;
      car.lastLapTime = st.lastLapTime;
      car.bestLapTime = st.bestLapTime;
    }

    // Classement : tri par progression décroissante.
    const order = cars.map((_, idx) => idx).sort((a, b) => this.states[b].progress - this.states[a].progress);
    order.forEach((idx, rank) => {
      cars[idx].position = rank + 1;
    });
  }
}

/** Intersection de deux segments [p1,p2] et [p3,p4]. */
function segmentsIntersect(
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  x3: number,
  y3: number,
  x4: number,
  y4: number,
): boolean {
  const d1 = cross(x3, y3, x4, y4, x1, y1);
  const d2 = cross(x3, y3, x4, y4, x2, y2);
  const d3 = cross(x1, y1, x2, y2, x3, y3);
  const d4 = cross(x1, y1, x2, y2, x4, y4);
  return ((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) && ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0));
}

/** Produit vectoriel (b-a) x (c-a). */
function cross(ax: number, ay: number, bx: number, by: number, cx: number, cy: number): number {
  return (bx - ax) * (cy - ay) - (by - ay) * (cx - ax);
}
